import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { checkAndStoreReferralCode } from '../../utils/referral'
import './Signup.scss'

function Signup() {
  const { loginWithPopup, isAuthenticated } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  // Ensure referral code is captured when visiting /signup?ref=CODE
  useEffect(() => {
    checkAndStoreReferralCode()
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleSignup = async () => {
    setIsLoading(true)
    try {
      const width = 700
      const height = 900
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2
      
      const popup = window.open(
        '',
        'auth0:authorize:popup',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
      )
      
      await loginWithPopup({
        authorizationParams: {
          screen_hint: 'signup'
        }
      }, { popup } as any)
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (error) {
      logger.error('Signup error', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="signup-page">
      <div className="signup-wrapper">
        <div className="signup-container">
          <div className="signup-header">
            <h1 className="signup-title">
              Start Your Journey to{' '}
              <span className="text-gradient">Better Job Applications</span>
            </h1>
            <p className="signup-subtitle">
              Join thousands of job seekers who are landing interviews faster with AI-powered resume tailoring, cover letter generation, and interview preparation.
            </p>
          </div>
          
          <div className="signup-card">
            <div className="card-header">
              <h3 className="card-title">Create Your Free Account</h3>
              <p className="card-description">
                Get started in seconds. No credit card required.
              </p>
              <p className="card-note">
                Sign up with a friend's referral link to get 2 extra credits!
              </p>
            </div>
            
            <div className="card-content">
              <button
                type="button"
                className="btn-modern btn-primary btn-large"
                aria-label="Sign up for JobsReadyAI"
                onClick={handleSignup}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                    Sign Up Free
                  </>
                )}
              </button>
              
              <div className="card-footer">
                <p className="footer-text">
                  Already have an account?{' '}
                  <a href="/" className="footer-link">
                    Go to home page
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h5 className="feature-title">AI-Powered</h5>
              <p className="feature-description">Smart resume tailoring and optimization</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h5 className="feature-title">Fast & Easy</h5>
              <p className="feature-description">Generate tailored applications in minutes</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h5 className="feature-title">Proven Results</h5>
              <p className="feature-description">Land more interviews with optimized applications</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Signup

